import type { RemoteNavigationPreviewCallback } from './useRemoteNavigationPreview';
import { useCallback, useEffect, useId, useRef, useState, type ReactElement } from 'react';
import { ApiError, parseApiErrorBody } from '@/api/v2Client';
import type { RemoteWireTarget } from '@/api/documentNavigation';
import { navigationTargets, type NavigationTarget, type SelectedTargetDetails, type TargetInventory } from '@/api/navigationTargets';
import { navStyles } from './navShared';
import { describeSavedRemoteMiss, matchSavedRemoteTarget } from './remoteSavedTarget';
import type { PdfPreviewFocus, PdfSelectionResult, SelectionKind } from './PdfCanvasViewer';

/** A previously-saved remote destination to re-select when the picker opens: its
 *  source-relative href plus the destination it recorded. Resolved against THIS
 *  picker's own inventory (one fetch, consistent ids), never a raw path fetch. */
export interface SavedRemoteSeed {
  readonly href: string;
  readonly destination:
    | { readonly kind: 'page'; readonly pageIndex: number; readonly at?: { readonly x: number; readonly y: number } }
    | { readonly kind: 'named'; readonly name: string };
  /** For an AUTHORED (bound) link, the server-CHECKED authorized tokens. The picker
   *  seeds the saved position ONLY if its own freshly-loaded inventory is still the
   *  SAME context and the target's hash still matches — otherwise the inventory /
   *  target moved since the envelope and it refuses to seed an old position onto a
   *  new version, requiring explicit reselection. Omitted for an imported link
   *  (href-based current-resolution). */
  readonly authorized?: { readonly targetId: string; readonly contextVersion: string; readonly expectedTargetSha256: string };
}

export interface RemoteTargetPickerProps {
  readonly documentId: string;
  readonly value: RemoteWireTarget | null;
  readonly onChange: (selection: RemoteWireTarget | null) => void;
  readonly idPrefix: string;
  readonly disabled?: boolean;
  /** Emits the verified target's exact bytes (as a Blob the caller owns) so a
   *  side-by-side workspace viewer can rasterise them; null clears the preview.
   *  The caller is responsible for the object-URL lifecycle of what it builds.
   *
   *  The third argument is the destination (page / named) the reviewer has
   *  currently chosen, expressed in the TARGET document's own coordinates, so the
   *  viewer shows that destination rather than page 1. Three call shapes:
   *    • (blob, label, focus)  — (re)load the exact bytes and show `focus`.
   *    • (null, label, focus)  — focus-only update: the bytes are unchanged, the
   *                              reviewer just picked a different page/name.
   *    • (null, '')            — clear the preview entirely. */
  readonly onPreview?: RemoteNavigationPreviewCallback;
  /** Arm a visual landing-point selection on the ALREADY-PREVIEWED target's own
   *  page (keepPreview keeps the cross-document bytes in the viewer). On a result
   *  the chosen page + landing point become this remote destination's `at`. */
  readonly onBeginSelect?: (kind: SelectionKind, onResult: (r: PdfSelectionResult) => void, opts?: { keepPreview?: boolean }) => void;
  /** When opening the picker to RE-TARGET a saved remote link, its saved target +
   *  destination. The picker resolves it against its own inventory and pre-selects
   *  the matching target (page/name + landing point), so "Change target" retains
   *  the existing destination instead of starting empty. A stale/ambiguous match
   *  is reported honestly and nothing is pre-selected. */
  readonly savedRemote?: SavedRemoteSeed | null;
}
function errorText(error: unknown): string {
  const body = parseApiErrorBody(error);
  const message = body?.message ?? body?.error ?? (error instanceof Error ? error.message : String(error));
  return error instanceof ApiError && error.status === 409
    ? `${message} — refresh the targets and re-choose the destination.` : message;
}

/** A selection becomes usable only after details from the exact selected bytes
 * arrive. The server repeats all token/destination checks at save time. */
export function RemoteTargetPicker({ documentId, value, onChange, idPrefix, disabled, onPreview, onBeginSelect, savedRemote }: RemoteTargetPickerProps): ReactElement {
  const previewOwner = useId();
  const [inventory, setInventory] = useState<TargetInventory | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [refresh, setRefresh] = useState(0);
  const [targetId, setTargetId] = useState<string | null>(value?.targetId ?? null);
  const [destKind, setDestKind] = useState<'page' | 'named'>(value?.destination.kind === 'named' ? 'named' : 'page');
  const [pageInput, setPageInput] = useState(value?.destination.kind === 'page' ? String(value.destination.pageIndex + 1) : '1');
  const [nameInput, setNameInput] = useState(value?.destination.kind === 'named' ? value.destination.name : '');
  // A visually-selected /XYZ landing point on the target page (page-destination
  // only); null ⇒ page-top. Cleared whenever the page/target/kind it belonged to
  // changes, so it can never apply to a different page.
  const [atPoint, setAtPoint] = useState<{ x: number; y: number } | null>(
    value?.destination.kind === 'page' && value.destination.at ? value.destination.at : null,
  );
  const [details, setDetails] = useState<{ targetId: string; data: SelectedTargetDetails } | null>(null);
  const [detailsError, setDetailsError] = useState<string | null>(null);
  // An honest note when a saved remote target can no longer be resolved (stale /
  // ambiguous / unavailable); the picker then starts empty rather than guessing.
  const [savedRemoteNote, setSavedRemoteNote] = useState<string | null>(null);
  // The inventory instance a saved-target auto-select has already run against, so
  // it runs once per load and never fights the reviewer's own later choice.
  const autoSelectedRef = useRef<TargetInventory | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const previewRef = useRef<string | null>(null);
  const previewEpoch = useRef(0);
  // The exact bytes last handed to the viewer — kept so a destination change can
  // re-focus the ALREADY-loaded preview without re-fetching.
  const previewBlobRef = useRef<Blob | null>(null);
  const changeRef = useRef(onChange);
  changeRef.current = onChange;
  const previewCbRef = useRef(onPreview);
  previewCbRef.current = onPreview;
  const selected = inventory?.targets.find(t => t.id === targetId) ?? null;
  const selectedDetails = details?.targetId === selected?.id ? details?.data ?? null : null;

  /** The chosen destination as a preview focus in the TARGET document's own
   *  coordinates, or null when the current destination input is not yet valid.
   *  Never an external URI — the picker only ever addresses a page or a named
   *  destination inside a verified sequence document. */
  const destinationFocus = useCallback((): PdfPreviewFocus | null => {
    if (!selected) return null;
    if (destKind === 'page') {
      const page = Number(pageInput);
      if (!Number.isInteger(page) || page < 1) return null;
      if (selectedDetails && page > selectedDetails.pageCount) return null;
      return { page, tone: 'destination', label: `Page ${page} in “${selected.label}”` };
    }
    const name = nameInput.trim();
    if (name.length === 0) return null;
    return { named: name, tone: 'destination', label: `“${name}” in “${selected.label}”` };
  }, [selected, destKind, pageInput, nameInput, selectedDetails]);
  // The LATEST destination focus, read at preview COMPLETION so a page/name the
  // reviewer changes while the fetch is pending is reflected — never the focus
  // captured in the request-start closure.
  const destinationFocusRef = useRef(destinationFocus);
  destinationFocusRef.current = destinationFocus;

  const clearPreview = useCallback(() => {
    previewEpoch.current += 1;
    if (previewRef.current) URL.revokeObjectURL(previewRef.current);
    previewRef.current = null;
    previewBlobRef.current = null;
    setPreviewUrl(null);
    setPreviewError(null);
    setPreviewing(false);
    previewCbRef.current?.(null, '', undefined, previewOwner);
  }, [previewOwner]);
  useEffect(() => {
    let cancelled = false;
    setInventory(null); setLoading(true); setLoadError(null);
    changeRef.current(null);
    navigationTargets.getInventory(documentId).then(result => {
      if (!cancelled) setInventory(result);
    }).catch((error: unknown) => {
      if (!cancelled) setLoadError(errorText(error));
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [documentId, refresh]);

  // Re-select a saved remote target once per inventory load: match its stored
  // href to a UNIQUE authorised target in THIS inventory (consistent ids/hash),
  // then seed the target + destination + landing point so "Change target" keeps
  // the existing destination. A stale/ambiguous/unavailable match is reported and
  // nothing is pre-selected (never a raw-path fetch, never a guess).
  useEffect(() => {
    if (!savedRemote || !inventory || autoSelectedRef.current === inventory) return;
    autoSelectedRef.current = inventory;
    const match = matchSavedRemoteTarget(inventory, savedRemote.href);
    if (match.kind !== 'ok') {
      setSavedRemoteNote(describeSavedRemoteMiss(match));
      return;
    }
    // For an AUTHORED (bound) link, the seed may only apply if THIS inventory is
    // still the SAME context AND the matched target's bytes still hash to the
    // server-checked value — otherwise the inventory/target moved since the
    // envelope and we must not seed the old position onto a new version (Kimi
    // stale read-window). Refuse and require explicit reselection.
    if (savedRemote.authorized) {
      if (inventory.contextVersion !== savedRemote.authorized.contextVersion || match.target.artifactSha256 !== savedRemote.authorized.expectedTargetSha256) {
        setSavedRemoteNote('the target document changed since this link was saved — reselect the destination to verify it');
        return;
      }
    }
    setSavedRemoteNote(null);
    setTargetId(match.target.id);
    const d = savedRemote.destination;
    if (d.kind === 'page') {
      setDestKind('page');
      setPageInput(String(d.pageIndex + 1));
      setAtPoint(d.at ?? null);
    } else {
      setDestKind('named');
      setNameInput(d.name);
      setAtPoint(null);
    }
  }, [savedRemote, inventory]);

  useEffect(() => {
    let cancelled = false;
    setDetails(null); setDetailsError(null); clearPreview();
    changeRef.current(null);
    if (inventory && selected?.available) {
      navigationTargets.details(documentId, selected.id, {
        contextVersion: inventory.contextVersion, expectedTargetSha256: selected.artifactSha256,
      }).then(data => {
        if (!cancelled) setDetails({ targetId: selected.id, data });
      }).catch((error: unknown) => { if (!cancelled) setDetailsError(errorText(error)); });
    }
    return () => { cancelled = true; previewEpoch.current += 1; };
  }, [documentId, inventory, selected, clearPreview]);
  useEffect(() => () => {
    previewEpoch.current += 1;
    if (previewRef.current) URL.revokeObjectURL(previewRef.current);
    // Clear the workspace preview THIS picker is showing so a stale target does
    // not linger once it unmounts — but only when this picker currently owns the
    // loaded bytes (previewBlobRef set). The owner token also prevents an older
    // picker from clearing a newer picker that has since taken over the viewer.
    if (previewBlobRef.current) previewCbRef.current?.(null, '', undefined, previewOwner);
  }, [previewOwner]);

  // Re-focus an ALREADY-loaded preview as the destination input changes — the
  // bytes are unchanged, so this is a focus-only update (no re-fetch). Guarded on
  // the stored blob: a target-document switch runs clearPreview first (which nulls
  // the ref synchronously), so this never re-focuses stale bytes for a new doc.
  useEffect(() => {
    if (!previewBlobRef.current) return;
    previewCbRef.current?.(null, selected?.label ?? '', destinationFocus(), previewOwner);
  }, [selected, destinationFocus, previewOwner]);

  useEffect(() => {
    let selection: RemoteWireTarget | null = null;
    if (!disabled && inventory && selected?.available && selectedDetails) {
      const page = Number(pageInput);
      const name = nameInput.trim();
      const validPage = Number.isInteger(page) && page >= 1 && page <= selectedDetails.pageCount;
      const validName = name.length > 0 && (!selectedDetails.namedDestinationsEnumerable || selectedDetails.namedDestinations?.includes(name));
      if ((destKind === 'page' && validPage) || (destKind === 'named' && validName)) selection = {
        kind: 'remoteTarget', targetId: selected.id, expectedContextVersion: inventory.contextVersion,
        expectedTargetSha256: selected.artifactSha256,
        destination: destKind === 'page'
          ? (atPoint ? { kind: 'page', pageIndex: page - 1, at: atPoint } : { kind: 'page', pageIndex: page - 1 })
          : { kind: 'named', name },
      };
    }
    changeRef.current(selection);
  }, [disabled, inventory, selected, selectedDetails, destKind, pageInput, nameInput, atPoint]);

  const runPreview = async (): Promise<void> => {
    if (!inventory || !selected?.available) return;
    const epoch = ++previewEpoch.current;
    setPreviewing(true); setPreviewError(null);
    try {
      const response = await navigationTargets.preview(documentId, selected.id, {
        contextVersion: inventory.contextVersion, expectedTargetSha256: selected.artifactSha256,
      });
      const blob = await response.blob();
      if (epoch !== previewEpoch.current) return;
      if (previewRef.current) URL.revokeObjectURL(previewRef.current);
      previewRef.current = URL.createObjectURL(blob);
      previewBlobRef.current = blob;
      setPreviewUrl(previewRef.current);
      // Hand the exact bytes to the workspace viewer (it builds/owns its own
      // object URL, so revoking ours here never breaks its render), together with
      // the destination the reviewer has chosen so the canvas opens THERE. The
      // focus is read from the ref at COMPLETION, so a page/name changed while the
      // fetch was pending is honoured rather than the request-start value.
      previewCbRef.current?.(blob, selected.label, destinationFocusRef.current(), previewOwner);
    } catch (error) {
      if (epoch !== previewEpoch.current) return;
      setPreviewError(errorText(error));
      if (error instanceof ApiError && error.status === 409) { setDetails(null); changeRef.current(null); }
    } finally { if (epoch === previewEpoch.current) setPreviewing(false); }
  };
  const refreshTargets = (): void => { setTargetId(null); setDetails(null); setAtPoint(null); clearPreview(); changeRef.current(null); setRefresh(n => n + 1); };
  const renderGroup = (label: string, targets: readonly NavigationTarget[]): ReactElement => <div style={{ marginTop: 6 }}>
    <div style={navStyles.label}>{label}</div>
    {targets.length === 0 ? <div style={navStyles.muted}>None.</div> : targets.map(target => <label key={target.id}
      data-testid={`${idPrefix}-remote-option-${target.id}`} style={{ display: 'flex', gap: 6, padding: '2px 0' }}>
      <input type="radio" name={`${idPrefix}-remote-target`} data-testid={`${idPrefix}-remote-radio-${target.id}`}
        checked={targetId === target.id} disabled={disabled || !target.available}
        onChange={() => { setTargetId(target.id); setDetails(null); setAtPoint(null); clearPreview(); changeRef.current(null); }} />
      <span>{target.label}{!target.available && <span data-testid={`${idPrefix}-remote-unavailable-${target.id}`} style={navStyles.muted}>
        {' '}· unavailable: {target.unavailableReason}</span>}</span>
    </label>)}
  </div>;

  if (loading) return <span style={navStyles.muted} data-testid={`${idPrefix}-remote-loading`}>Loading link targets…</span>;
  return <div style={{ ...navStyles.note, display: 'block' }} data-testid={`${idPrefix}-remote-picker`}>
    <div style={navStyles.muted}>Choose a document from this sequence or a prior delivered sequence. The destination is verified against its exact contents.</div>
    <button type="button" style={disabled ? navStyles.secondaryBtnDisabled : navStyles.secondaryBtn} disabled={disabled} onClick={refreshTargets}>Refresh targets</button>
    {loadError && <div role="alert" data-testid={`${idPrefix}-remote-error`} style={navStyles.error}>Could not load link targets: {loadError}</div>}
    {savedRemoteNote && <div role="alert" data-testid={`${idPrefix}-remote-saved-note`} style={{ ...navStyles.note, color: 'var(--warning, #b45309)' }}>Previously-saved target: {savedRemoteNote}.</div>}
    {inventory && <>{renderGroup('This submission (current sequence)', inventory.targets.filter(t => t.scope === 'current'))}
      {renderGroup('Prior delivered sequences', inventory.targets.filter(t => t.scope === 'prior'))}</>}
    {selected && <div style={{ marginTop: 8 }} data-testid={`${idPrefix}-remote-dest`}>
      <div style={navStyles.label}>Destination in “{selected.label}”</div>
      {detailsError && <div role="alert" style={navStyles.error}>{detailsError}</div>}
      <div data-testid={`${idPrefix}-remote-pagecount`} style={navStyles.muted}>
        {selectedDetails ? `Target has ${selectedDetails.pageCount} pages.` : detailsError ? 'Destination details unavailable. Refresh to choose again.' : 'Reading destination details…'}
      </div>
      <select aria-label="Remote destination kind" data-testid={`${idPrefix}-remote-dest-kind`} value={destKind}
        disabled={disabled || !selectedDetails} style={navStyles.input} onChange={e => { setAtPoint(null); setDestKind(e.target.value === 'named' ? 'named' : 'page'); }}>
        <option value="page">Page</option><option value="named">Named destination</option>
      </select>
      {destKind === 'page' ? <input aria-label="Remote destination page" data-testid={`${idPrefix}-remote-dest-page`}
        type="number" min={1} max={selectedDetails?.pageCount} value={pageInput} disabled={disabled || !selectedDetails}
        style={{ ...navStyles.input, width: 72 }} onChange={e => { setAtPoint(null); setPageInput(e.target.value); }} />
        : selectedDetails?.namedDestinationsEnumerable ? <select aria-label="Remote named destination" data-testid={`${idPrefix}-remote-dest-name`}
          value={nameInput} disabled={disabled} style={navStyles.input} onChange={e => setNameInput(e.target.value)}>
          <option value="">Choose a destination</option>{selectedDetails.namedDestinations?.map(name => <option key={name} value={name}>{name}</option>)}
        </select> : <input aria-label="Remote named destination" data-testid={`${idPrefix}-remote-dest-name`} type="text" value={nameInput}
          disabled={disabled || !selectedDetails} style={navStyles.input} onChange={e => setNameInput(e.target.value)} />}
      {destKind === 'named' && !selectedDetails?.namedDestinationsEnumerable && <div style={navStyles.muted}>Enter the exact destination name; it will be verified before saving.</div>}
      <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
        <button type="button" data-testid={`${idPrefix}-remote-preview`}
          style={disabled || previewing || !selectedDetails ? navStyles.secondaryBtnDisabled : navStyles.secondaryBtn}
          disabled={disabled || previewing || !selectedDetails} onClick={() => void runPreview()}>{previewing ? 'Loading preview…' : 'Preview target PDF'}</button>
        {previewUrl && <><a href={previewUrl} target="_blank" rel="noopener noreferrer" data-testid={`${idPrefix}-remote-preview-open`}>Open the target in a new tab</a>
          <a href={previewUrl} download={selected.leafPath.split('/').pop() ?? 'target.pdf'} data-testid={`${idPrefix}-remote-preview-download`}>Download the target</a></>}
      </div>
      {/* Landing point on the AUTHORIZED TARGET's own page. "Pick…" (draw it on the
          exact-bytes preview) needs the viewer + a loaded preview; the CURRENT point
          (visually picked now, or seeded from a reopened saved link) is shown and
          clearable regardless, so a saved landing point is always visible. */}
      {destKind === 'page' && (onBeginSelect || atPoint) && (
        <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap', alignItems: 'center' }}>
          {onBeginSelect && (
            <button
              type="button"
              data-testid={`${idPrefix}-remote-select-landing`}
              style={disabled || !previewUrl ? navStyles.secondaryBtnDisabled : navStyles.secondaryBtn}
              disabled={disabled || !previewUrl}
              title={!previewUrl ? 'Load the exact-bytes preview first, then draw the landing point on it' : undefined}
              onClick={() =>
                onBeginSelect('destination', (r) => { setPageInput(String(r.page)); setAtPoint({ x: r.rect.x0, y: r.rect.y1 }); }, { keepPreview: true })
              }
            >
              Select destination…
            </button>
          )}
          {atPoint && (
            <span style={navStyles.muted} data-testid={`${idPrefix}-remote-landing-at`}>
              landing at ({atPoint.x.toFixed(0)}, {atPoint.y.toFixed(0)}){' '}
              <button type="button" data-testid={`${idPrefix}-remote-landing-clear`} style={navStyles.smallBtn} disabled={disabled} onClick={() => setAtPoint(null)}>clear</button>
            </span>
          )}
        </div>
      )}
      {previewError && <div role="alert" style={navStyles.error} data-testid={`${idPrefix}-remote-preview-error`}>{previewError}</div>}
    </div>}
  </div>;
}
export default RemoteTargetPicker;
